import { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Coins } from "lucide-react";

const STAGES = ["", "Seed", "Series A", "Series B", "Growth"];

export default function Earn() {
  const [role, setRole] = useState("");
  const [stage, setStage] = useState("");
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState("");

  const load = async () => {
    const qs = [];
    if (role) qs.push(`role=${encodeURIComponent(role)}`);
    if (stage) qs.push(`stage=${encodeURIComponent(stage)}`);
    const { data } = await api.get(`/earn/benchmarks${qs.length ? "?" + qs.join("&") : ""}`);
    setRows(data.items || []);
    setNote(data.disclaimer || "");
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stage]);

  return (
    <div className="space-y-10" data-testid="earn-page">
      <div>
        <div className="label-caps mb-2 flex items-center gap-1.5"><Coins className="h-3 w-3 text-amber-400" />Pillar · Earn</div>
        <h1 className="text-3xl sm:text-5xl tracking-tight font-semibold">What the market pays.</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Base, equity and bonus ranges by role and stage. Indicative US bands in USD.
        </p>
      </div>

      <Card className="p-4 flex flex-col md:flex-row gap-3">
        <Input placeholder="Role filter (e.g. CTO)" value={role} onChange={(e) => setRole(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()} data-testid="earn-role-filter" />
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="md:w-48" data-testid="earn-stage"><SelectValue placeholder="Any stage" /></SelectTrigger>
          <SelectContent>
            {STAGES.map((s) => <SelectItem key={s || "all"} value={s || " "}>{s || "Any stage"}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={load} className="rounded-full" data-testid="earn-apply">Apply</Button>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="earn-table">
            <thead className="bg-secondary/40">
              <tr className="label-caps text-left">
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Stage</th>
                <th className="px-4 py-3">Region</th>
                <th className="px-4 py-3 text-right">Base (USD k)</th>
                <th className="px-4 py-3 text-right">Equity %</th>
                <th className="px-4 py-3 text-right">Bonus %</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No matches.</td></tr>
              ) : rows.map((r, i) => (
                <tr key={i} className="border-t border-border hover:bg-secondary/20">
                  <td className="px-4 py-3 font-medium">{r.role}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.stage}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.region}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">${r.base_min_k}–${r.base_max_k}k</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{r.equity_min_pct}–{r.equity_max_pct}%</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{r.bonus_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-xs text-muted-foreground">{note}</p>
    </div>
  );
}
